import React from 'react'
import Image from 'next/image'
import { s3Static } from '../../../../utils/constants'
import { motion } from 'framer-motion'
import { FaStar, FaQuoteLeft, FaHeart, FaDumbbell, FaCheckCircle } from 'react-icons/fa'
import { HiSparkles } from 'react-icons/hi'
import Link from 'next/link'

export const ClientStoryNisha = () => {
    return (
        <section className="py-16 bg-linear-to-br from-purple-50 via-pink-50 to-orange-50 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4">
                {/* Section Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <HiSparkles className="text-amber-500 text-3xl" />
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Real Client, Real Transformation
                        </h2>
                        <HiSparkles className="text-amber-500 text-3xl" />
                    </div>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        Meet Nisha, a 35-year-old working professional from Singapore who transformed her life through dedicated training with certified personal trainer Nur Faridah
                    </p>
                </div>

                {/* Main Content Grid */}
                <div className="grid md:grid-cols-2 gap-12 items-start">
                    {/* Left Column: Image + Client Info */}
                    <div className="space-y-6">
                        {/* Image Section */}
                        <div className="relative">
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                                {/* Decorative elements */}
                                <div className="absolute -top-4 -left-4 w-24 h-24 bg-linear-to-br from-pink-400 to-purple-500 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-linear-to-br from-orange-400 to-pink-500 rounded-full blur-2xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }}></div>

                                {/* Image placeholder - will be replaced with actual client photo */}
                                <div className="relative h-96 md:h-120 bg-linear-to-br from-purple-100 to-pink-100">
                                    <Image
                                        src={s3Static + "personal-trainer/generated/nur-faridah-training-nisha.png"}
                                        fill
                                        style={{ objectFit: 'cover', objectPosition: 'top' }}
                                        alt="Nisha training with Personal Trainer Nur Faridah at condominium gym in Singapore"
                                        className="rounded-3xl"
                                        sizes="(max-width: 768px) 100vw, 50vw"
                                    />
                                </div>

                                {/* Floating badge */}
                                <motion.div
                                    animate={{ y: [0, -10, 0] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                    className="absolute top-6 right-6 bg-white rounded-full px-4 py-2 shadow-lg"
                                >
                                    <div className="flex items-center gap-2">
                                        <FaHeart className="text-red-500" />
                                        <span className="font-bold text-gray-800">Success Story</span>
                                    </div>
                                </motion.div>
                            </div>

                            {/* Stats Cards */}
                            <div className="grid grid-cols-2 gap-4 mt-6">
                                <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
                                    <div className="text-3xl font-bold text-purple-600">12kg</div>
                                    <div className="text-sm text-gray-600">Weight Lost</div>
                                </div>
                                <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
                                    <div className="text-3xl font-bold text-pink-600">6 Months</div>
                                    <div className="text-sm text-gray-600">Journey Time</div>
                                </div>
                            </div>
                        </div>

                        {/* Client Info Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-purple-500">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900">Nisha Kumar</h3>
                                    <p className="text-gray-600">Marketing Manager, 35 years old</p>
                                    <p className="text-sm text-gray-500 mt-1">📍 Training Location: Condominium Gym, Central Singapore</p>
                                </div>
                                <div className="flex gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <FaStar key={i} className="text-amber-400 text-lg" />
                                    ))}
                                </div>
                            </div>

                            {/* Quote */}
                            <div className="relative bg-linear-to-r from-purple-50 to-pink-50 rounded-xl p-4 mt-4">
                                <FaQuoteLeft className="text-purple-300 text-3xl absolute top-2 left-2 opacity-50" />
                                <p className="text-gray-700 italic pl-8 leading-relaxed">
                                    &quot;I struggled with weight gain for years and felt self-conscious. The trial session with Nur Faridah changed everything. She understood my challenges as an Indian woman with dietary preferences and created a program that worked for my lifestyle. Six months later, I&apos;m 12kg lighter, healthier, and more confident than ever!&quot;
                                </p>
                                <div className="text-right mt-2">
                                    <span className="text-purple-600 font-semibold">— Nisha K.</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Story Content */}
                    <div className="space-y-6">
                        {/* Journey Timeline */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg space-y-4">
                            <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <FaDumbbell className="text-orange-500" />
                                Nisha&apos;s Transformation Journey
                            </h4>

                            <div className="space-y-4">
                                {[
                                    {
                                        title: "Starting Point - Free Consultation",
                                        desc: "Nisha, a mid-weight professional feeling overwhelmed by weight gain, booked a complimentary consultation after seeing StringsSG's affordable packages. Nur Faridah conducted a comprehensive health assessment and understood her cultural dietary needs.",
                                        icon: "🎯"
                                    },
                                    {
                                        title: "Personalized Program",
                                        desc: "Nur designed a culturally-sensitive fitness plan combining strength training and cardio at Nisha's condo gym. The program included modifications for Indian cuisine and respected her work schedule, making it sustainable and affordable at just $320/month.",
                                        icon: "📋"
                                    },
                                    {
                                        title: "Consistent Support",
                                        desc: "Training 2x per week at her convenient condo gym location, Nisha received ongoing encouragement from both Nur Faridah and the StringsSG support team. The personalized attention helped her stay motivated during challenging weeks.",
                                        icon: "💪"
                                    },
                                    {
                                        title: "Life-Changing Results",
                                        desc: "In 6 months, Nisha lost 12kg, gained muscle tone, improved her energy levels, and boosted her confidence. She now maintains her healthy lifestyle independently while having ongoing support from her trainer when needed.",
                                        icon: "🌟"
                                    }
                                ].map((step, index) => (
                                    <div key={index} className="flex gap-4 items-start">
                                        <div className="shrink-0 w-12 h-12 bg-linear-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xl shadow-lg">
                                            {step.icon}
                                        </div>
                                        <div className="grow">
                                            <h5 className="font-bold text-gray-900 mb-1">{step.title}</h5>
                                            <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Key Benefits */}
                        <div className="bg-linear-to-r from-green-50 to-emerald-50 rounded-2xl p-6 shadow-lg">
                            <h4 className="font-bold text-gray-900 mb-4">What Made the Difference:</h4>
                            <div className="space-y-3">
                                {[
                                    "Trial session removed barriers to starting",
                                    "Affordable package ($80/session) fit her budget",
                                    "Culturally-sensitive approach to diet and exercise",
                                    "Convenient condo gym location saved travel time",
                                    "Personal care from certified trainer and support team",
                                    "Sustainable program that fit her lifestyle"
                                ].map((benefit, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-3"
                                    >
                                        <FaCheckCircle className="text-green-500 shrink-0" />
                                        <span className="text-gray-700">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Trainer Info */}
                        <div className="bg-linear-to-r from-purple-600 to-pink-600 rounded-2xl p-6 shadow-lg text-white">
                            <div className="flex items-center gap-4">
                                <div className="relative w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-lg shrink-0">
                                    <Image
                                        src={s3Static + "personal-trainer/generated/Nur Faridah PT.jpeg"}
                                        fill
                                        style={{ objectFit: 'cover' }}
                                        alt="Personal Trainer Nur Faridah"
                                        sizes="80px"
                                    />
                                </div>
                                <div>
                                    <h5 className="font-bold text-xl">Nur Faridah</h5>
                                    <p className="text-purple-100 text-sm">NASM Certified Personal Trainer</p>
                                    <p className="text-purple-100 text-sm">Women&apos;s Fitness Specialist</p>
                                    <div className="flex gap-1 mt-2">
                                        {[...Array(5)].map((_, i) => (
                                            <FaStar key={i} className="text-amber-300" />
                                        ))}
                                        <span className="ml-2 text-sm">4.9/5.0</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* CTA */}
                <div
                    className="text-center pt-10"
                >
                    <Link
                        href="/sg/personal-trainer"
                        className="inline-block bg-linear-to-r from-orange-600 to-pink-600 text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                        Start Your Free Consultation Today →
                    </Link>
                    <p className="text-sm text-gray-600 mt-3">
                        Start the way Nisha did, with a free consultation
                    </p>
                </div>
                {/* YMYL Compliance Footer */}
                <div
                    className="mt-12 bg-blue-50 rounded-2xl p-6 border-l-4 border-blue-500"
                >
                    <p className="text-sm text-gray-700 leading-relaxed">
                        <strong>Medical Disclaimer:</strong> Individual results may vary. Nisha&apos;s transformation was achieved through dedicated training with a certified personal trainer, proper nutrition, and lifestyle changes over 6 months. StringsSG trainers are certified professionals but are not medical doctors. Consult your physician before beginning any exercise program, especially if you have pre-existing health conditions. Weight loss results depend on individual effort, consistency, starting point, and overall health status.
                    </p>
                </div>
            </div>
        </section>
    )
}
